import { Option } from '@app/forms/formly/formly-utils';
import {
  PortalCreditCardNode,
  PortalOrderNode,
  PortalOrderQuoteCardNode,
  PortalPassengerNode,
} from '@app/services/api/api.types';
import { toLabel } from '@app/ui/pipes/label.pipe';
import { PortalRoutes } from '@app/app-routing.module';
import { State } from '@app/services/state/state.service';
import { getTierRetentionPrice } from '@app/pages/tiers-page/utils';
import { getBasicChangeFeeTotal } from '@app/utils/basic-change-fee';

export function isEmbeddedInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function shouldHideButtonsInEmbeddedMode(nextRoute: PortalRoutes): boolean {
  return isEmbeddedInIframe() && nextRoute !== PortalRoutes.Tiers;
}

export const scrollTo = (el: Element | string, behavior: ScrollBehavior = 'smooth') => {
  setTimeout(() => {
    if (typeof el === 'string') {
      el = document.querySelector(el) as Element;
    }

    el?.scrollIntoView({ behavior });
  });
};

export const setMaxHeight = () => {
  const vh = Math.floor(window.innerHeight * 0.01 * 100);
  document.documentElement.style.setProperty('--mobile-modal-max-height', `calc(${vh}px - 32px)`);
};

export const mapPortalPassengerNode = (p: PortalPassengerNode) => {
  return {
    value: p.id,
    label: p.fullName,
    extra: {
      firstName: p.firstName,
      middleName: p.middleName,
      lastName: p.lastName,
      birthday: p.birthday,
      gender: p.gender,
      passportCountryId: p.countryId,
    },
  } as Option;
};

export const mapPortalCreditCardNode = (c: PortalCreditCardNode) => {
  return {
    value: c.id,
    label: `${toLabel(c.type)} **** ${c.number.slice(-4)}`,
    extra: {
      holder: c.holder,
      expiration: c.expiration,
    },
  } as Option;
};

export const getDomain = (email = '') => {
  const regex = /^([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
  const match = email.match(regex) as string[];

  if (match) {
    const domain = match[2].toLowerCase();

    if (domain.includes('gmail.com')) {
      return { label: 'Gmail', url: 'https://gmail.com' };
    } else if (domain.includes('yahoo.com') || domain.includes('myyahoo.com')) {
      return { label: 'Yahoo', url: 'https://mail.yahoo.com' };
    } else if (
      domain.includes('outlook.com') ||
      domain.includes('hotmail.com') ||
      domain.includes('live.com') ||
      domain.includes('msn.com')
    ) {
      return { label: 'Outlook', url: 'https://outlook.live.com' };
    }
  }

  return null;
};

export function getFormatedCurrency(
  value: number,
  currencyCode: string = 'USD',
  locale: string = 'en-US',
  rounded: boolean = false,
): string {
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: rounded || Number(value) === Math.round(value) ? 0 : 2,
    maximumFractionDigits: rounded || Number(value) === Math.round(value) ? 0 : 2,
  };

  return new Intl.NumberFormat(locale, options).format(value);
}

export function calculateMaxBalanceAmount(
  maxBalanceAmount: number = 0,
  clientBalanceAmount: number = 0,
  state: State,
  portalOrder?: PortalOrderNode | null,
) {
  const additionalProductsTotal = calculateAdditionalProductsTotal(state, portalOrder);
  const basicChangeFeeTotal = getBasicChangeFeeTotal(portalOrder);

  const total = maxBalanceAmount + additionalProductsTotal + basicChangeFeeTotal;
  return Math.min(total, clientBalanceAmount);
}

export function calculateAdditionalProductsTotal(
  state: State,
  portalOrder?: PortalOrderNode | null,
) {
  return (
    (state.disruptionProtection || 0) +
    (state.tipsAmount || 0) +
    (state.bundle?.price || 0) +
    (state.cancelForAnyReason || 0) +
    getTierRetentionPrice(portalOrder ?? null, state.tier)
  );
}

export function getOptionSoldAmount(option: PortalOrderNode | PortalOrderQuoteCardNode): number {
  const soldAmount = option.totalPassengers ? option.soldAmountPerAdult : option.soldAmount;
  return soldAmount ?? 0;
}
